import React, { useEffect, useState } from 'react';

import { Box, createStyles, Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconPinned, IconPinnedFilled, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useNotes from 'hooks/notes/useNotes';
import { CellProps } from 'react-table';
import { NoteList } from 'types';

import NoteDeleteModal from './NoteDeleteModal';

const useStyles = createStyles({
  titleColumn: {
    width: '20%',
  },
  contentColumn: {
    width: '60%',
  },
  createdAtColumn: {
    width: '8.75%',
  },
  createdByColumn: {
    width: '8.75%',
  },
  actionsColumn: {
    width: '2.5%',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
  },
});

const AthleteNotesTable = ({
  athleteId,
  searchTerm,
  handleViewNote,
  permission = true,
  margin = 12,
  bgc = 'white',
}: {
  athleteId: string;
  searchTerm?: string;
  handleViewNote: (id: string) => void;
  permission?: boolean;
  margin?: number;
  bgc?: 'white' | 'gray';
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { dateTimeFormat } = useLocalization();

  const [ascending, setAscending] = useState(true);
  const [noteId, setNoteId] = useState<string>('');

  const { data, isSuccess, isError, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotes({
    athleteId: athleteId,
    search: searchTerm,
    ordering: ascending ? 'createdAt' : '-createdAt',
  });

  const [tableData, setTableData] = useState<NoteList[]>([]);
  const [isDeleteModalOpened, { toggle: handleDeleteModalToggle }] = useDisclosure(false);

  const handleDelete = (note: NoteList): void => {
    setNoteId(note.noteId);
    handleDeleteModalToggle();
  };

  const columns = [
    {
      Header: 'Title',
      accessor: 'title',
      cellClassName: classes.titleColumn,
      Cell: ({ row }: CellProps<NoteList>) => <Text lineClamp={5}>{row.original.title}</Text>,
    },
    {
      Header: 'Note',
      accessor: 'content',
      cellClassName: classes.contentColumn,
      Cell: ({ row }: CellProps<NoteList>) => (
        <Text
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'break-spaces',
          }}
          lineClamp={5}
          dangerouslySetInnerHTML={{ __html: row.original.content }}
        />
      ),
      minWidth: '200px',
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      cellClassName: classes.createdAtColumn,
      Cell: ({ value }: CellProps<NoteList>): JSX.Element => {
        return <>{dayjs(value).format(dateTimeFormat)}</>;
      },
    },
    {
      Header: 'Pin',
      accesssor: 'pinned',
      Cell: ({ row }: CellProps<NoteList>) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {row.original.pinned ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.green[7] }}>
                <IconPinnedFilled size={20} /> On
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.gray[7] }}>
                <IconPinned size={20} /> Off
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      Header: 'Created By',
      accessor: 'createdBy.fullName',
      cellClassName: classes.createdByColumn,
    },
    {
      id: 'actions',
      disableSortBy: true,
      cellClassName: classes.actionsColumn,
      Cell: ({ row }: CellProps<NoteList>) => {
        return (
          <Group position='right'>
            <Menu
              disabled={!permission}
              items={[
                {
                  title: 'View',
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                  onClick: () => handleViewNote?.(row.original.noteId),
                },
                {
                  title: 'Delete',
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: 'red',
                  onClick: () => handleDelete(row.original),
                },
              ]}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isError={isError}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={!!hasNextPage}
        clickableHeaders={['createdAt']}
        handleHeaderClick={() => setAscending(!ascending)}
        ascending={ascending}
        ordering='createdAt'
        margin={margin}
        bgc={bgc}
        handleRowClick={row => handleViewNote(row.noteId)}
        rowClassName={() => classes.clickableRow}
      />
      <NoteDeleteModal opened={isDeleteModalOpened} noteId={noteId} toggleDrawer={handleDeleteModalToggle} />
    </>
  );
};

export default AthleteNotesTable;
